import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MenuItem {
  id: number;
  name: string;
  nameEn: string;
  nameAr: string;
  description?: string;
  descriptionEn?: string;
  descriptionAr?: string;
  price: string;
  icon: string;
  image: string;
  popular?: boolean;
}

export default function Menu() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  const categories = [
    { id: 'kahve', name: t('menu.coffee'), icon: 'ri-fire-fill' },
    { id: 'cay', name: t('menu.tea'), icon: 'ri-cup-fill' },
    { id: 'soguk', name: t('menu.cold'), icon: 'ri-goblet-fill' },
    { id: 'pasta', name: t('menu.dessert'), icon: 'ri-cake-2-fill' }
  ];

  const menuItems: Record<string, MenuItem[]> = {
    kahve: [
      {
        id: 1,
        name: 'Türk Kahvesi',
        nameEn: 'Turkish Coffee',
        nameAr: 'قهوة تركية',
        description: 'Geleneksel közde pişirme yöntemiyle hazırlanan, köpüğü bol ve aroması eşsiz Türk kahvemiz. Közün eşit ısısında yavaş yavaş pişirilerek kahvenin gerçek tadı ortaya çıkarılır.',
        descriptionEn: 'Our Turkish coffee prepared with traditional charcoal brewing method, rich foam and unique aroma. Slowly cooked over even charcoal heat to bring out the true taste of coffee.',
        descriptionAr: 'قهوتنا التركية المحضرة بطريقة الطهي التقليدية على الفحم، برغوة غنية ورائحة فريدة. يتم طهيها ببطء على حرارة الفحم المتساوية لإبراز المذاق الحقيقي للقهوة.',
        price: '120 ₺',
        icon: 'ri-fire-fill',
        image: 'https://public.readdy.ai/ai/img_res/36cf8c2f164aa80e18f0b6f0bf1cec65.jpg',
        popular: true
      },
      {
        id: 2,
        name: 'Türk Kahvesi (Duble)',
        nameEn: 'Turkish Coffee (Double)',
        nameAr: 'قهوة تركية (مزدوجة)',
        description: 'Kahve tutkunları için özel olarak hazırlanan duble porsiyonumuz. İki kat lezzet, iki kat keyif. Közde pişirme sanatının en güzel örneği.',
        descriptionEn: 'Our double portion specially prepared for coffee enthusiasts. Double the flavor, double the pleasure. The finest example of charcoal brewing art.',
        descriptionAr: 'حصتنا المزدوجة المحضرة خصيصاً لعشاق القهوة. ضعف النكهة، ضعف المتعة. أفضل مثال على فن الطهي على الفحم.',
        price: '200 ₺',
        icon: 'ri-fire-fill',
        image: 'https://readdy.ai/api/search-image?query=double%20serving%20traditional%20turkish%20coffee%20in%20two%20ornate%20copper%20cups%20with%20rich%20foam%20served%20together%20on%20elegant%20brass%20tray%20with%20sugar%20cubes%20simple%20white%20background%20professional%20food%20photography%20warm%20ambient%20lighting%20side%20angle&width=400&height=400&seq=menu-turk-duble-002&orientation=squarish'
      },
      {
        id: 3,
        name: 'Damla Sakızlı Kahve',
        nameEn: 'Mastic Gum Coffee',
        nameAr: 'قهوة بالمستكة',
        description: 'Sakız adasının eşsiz aroması ile harmanlanmış özel kahvemiz. Damla sakızının ferahlatıcı tadı ve kahvenin yoğun aroması mükemmel bir uyum içinde.',
        descriptionEn: 'Our special coffee blended with the unique aroma of Chios mastic. The refreshing taste of mastic gum and the intense aroma of coffee in perfect harmony.',
        descriptionAr: 'قهوتنا الخاصة الممزوجة برائحة المستكة الفريدة من جزيرة خيوس. طعم المستكة المنعش ورائحة القهوة القوية في تناغم مثالي.',
        price: '120 ₺',
        icon: 'ri-fire-fill',
        image: 'https://readdy.ai/api/search-image?query=aromatic%20mastic%20gum%20coffee%20in%20traditional%20turkish%20cup%20with%20creamy%20foam%20and%20mastic%20resin%20pieces%20beside%20cup%20served%20on%20wooden%20surface%20simple%20clean%20background%20professional%20beverage%20photography%20soft%20natural%20lighting%20close%20up&width=400&height=400&seq=menu-damla-sakiz-003&orientation=squarish',
        popular: true
      },
      {
        id: 4,
        name: 'Dibek Kahvesi',
        nameEn: 'Dibek Coffee',
        nameAr: 'قهوة ديبك',
        description: 'Taş havanda öğütülen geleneksel dibek kahvemiz. Yoğun kıvamı ve kremsi dokusuyla damakta unutulmaz bir tat bırakır. Anadolu\'nun kadim lezzeti.',
        descriptionEn: 'Our traditional dibek coffee ground in stone mortar. Leaves an unforgettable taste with its thick consistency and creamy texture. Ancient flavor of Anatolia.',
        descriptionAr: 'قهوة ديبك التقليدية المطحونة في هاون حجري. تترك طعماً لا يُنسى بقوامها الكثيف وملمسها الكريمي. نكهة الأناضول القديمة.',
        price: '120 ₺',
        icon: 'ri-fire-fill',
        image: 'https://readdy.ai/api/search-image?query=traditional%20dibek%20coffee%20stone%20ground%20coffee%20in%20rustic%20ceramic%20cup%20with%20thick%20creamy%20texture%20and%20stone%20mortar%20in%20background%20served%20on%20wooden%20board%20simple%20neutral%20background%20authentic%20turkish%20coffee%20photography%20warm%20tones%20overhead%20view&width=400&height=400&seq=menu-dibek-004&orientation=squarish'
      },
      {
        id: 5,
        name: 'Dibek Kahvesi (Duble)',
        nameEn: 'Dibek Coffee (Double)',
        nameAr: 'قهوة ديبك (مزدوجة)',
        description: 'Dibek kahvesinin yoğun lezzetini duble porsiyonda sunuyoruz. Taş havanda öğütülen kahvenin eşsiz tadını iki kat keyifle deneyimleyin.',
        descriptionEn: 'We serve the intense flavor of dibek coffee in double portion. Experience the unique taste of stone-ground coffee with double pleasure.',
        descriptionAr: 'نقدم النكهة القوية لقهوة ديبك في حصة مزدوجة. جرب الطعم الفريد للقهوة المطحونة بالحجر مع متعة مضاعفة.',
        price: '200 ₺',
        icon: 'ri-fire-fill',
        image: 'https://readdy.ai/api/search-image?query=double%20portion%20dibek%20coffee%20two%20rustic%20ceramic%20cups%20filled%20with%20stone%20ground%20coffee%20rich%20creamy%20texture%20served%20together%20on%20wooden%20tray%20simple%20background%20traditional%20turkish%20beverage%20photography%20natural%20lighting%20diagonal%20composition&width=400&height=400&seq=menu-dibek-duble-005&orientation=squarish'
      },
      {
        id: 6,
        name: 'Menengiç Kahvesi',
        nameEn: 'Terebinth Coffee',
        nameAr: 'قهوة المننجيتش',
        description: 'Güneydoğu Anadolu\'nun geleneksel içeceği menengiç kahvesi. Kafeinsiz ve doğal, fıstık aromalı bu özel içecek sağlıklı bir alternatif sunar.',
        descriptionEn: 'Traditional beverage of Southeast Anatolia, terebinth coffee. Caffeine-free and natural, this special drink with pistachio aroma offers a healthy alternative.',
        descriptionAr: 'المشروب التقليدي لجنوب شرق الأناضول، قهوة المننجيتش. خالية من الكافيين وطبيعية، هذا المشروب الخاص برائحة الفستق يقدم بديلاً صحياً.',
        price: '120 ₺',
        icon: 'ri-fire-fill',
        image: 'https://readdy.ai/api/search-image?query=traditional%20terebinth%20coffee%20menengic%20kahvesi%20in%20clear%20glass%20cup%20with%20reddish%20brown%20color%20and%20natural%20foam%20served%20with%20terebinth%20beans%20scattered%20around%20simple%20white%20background%20authentic%20turkish%20beverage%20photography%20soft%20lighting%20front%20view&width=400&height=400&seq=menu-menengic-006&orientation=squarish'
      },
      {
        id: 7,
        name: 'Nescafe',
        nameEn: 'Nescafe',
        nameAr: 'نسكافيه',
        description: 'Klasik hazır kahve sevenler için özenle hazırlanmış nescafe. Sıcak sütüyle harmanlanmış, köpüklü ve lezzetli bir kahve deneyimi.',
        descriptionEn: 'Carefully prepared nescafe for classic instant coffee lovers. Blended with hot milk, a foamy and delicious coffee experience.',
        descriptionAr: 'نسكافيه محضر بعناية لعشاق القهوة الفورية الكلاسيكية. ممزوج بالحليب الساخن، تجربة قهوة رغوية ولذيذة.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=instant%20coffee%20nescafe%20in%20modern%20white%20ceramic%20mug%20with%20rich%20brown%20color%20and%20light%20foam%20served%20on%20clean%20marble%20surface%20with%20coffee%20spoon%20simple%20minimalist%20background%20professional%20beverage%20photography%20bright%20natural%20lighting&width=400&height=400&seq=menu-nescafe-007&orientation=squarish'
      },
      {
        id: 8,
        name: 'Cappuccino',
        nameEn: 'Cappuccino',
        nameAr: 'كابتشينو',
        description: 'İtalyan kahve kültürünün vazgeçilmezi cappuccino. Espresso, sıcak süt ve mikro köpüğün mükemmel dengesi. Üzerindeki latte art ile görsel bir şölen.',
        descriptionEn: 'The indispensable of Italian coffee culture, cappuccino. Perfect balance of espresso, hot milk and microfoam. A visual feast with latte art on top.',
        descriptionAr: 'الكابتشينو، العنصر الأساسي في ثقافة القهوة الإيطالية. توازن مثالي من الإسبريسو والحليب الساخن والرغوة الدقيقة. وليمة بصرية مع فن اللاتيه في الأعلى.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=classic%20cappuccino%20in%20white%20ceramic%20cup%20with%20perfect%20microfoam%20milk%20art%20rosetta%20design%20on%20top%20served%20on%20matching%20saucer%20simple%20clean%20background%20professional%20coffee%20shop%20photography%20soft%20overhead%20lighting%20artistic%20presentation&width=400&height=400&seq=menu-cappuccino-008&orientation=squarish'
      },
      {
        id: 9,
        name: 'Caffe Latte',
        nameEn: 'Caffe Latte',
        nameAr: 'كافيه لاتيه',
        description: 'Yumuşak ve kremsi dokusuyla caffe latte. Espresso ve buharda pişirilmiş sütün zarif birleşimi. Her yudumda hissedeceğiniz kadifemsi lezzet.',
        descriptionEn: 'Caffe latte with its smooth and creamy texture. Elegant combination of espresso and steamed milk. Velvety flavor you will feel in every sip.',
        descriptionAr: 'كافيه لاتيه بملمسه الناعم والكريمي. مزيج أنيق من الإسبريسو والحليب المبخر. نكهة مخملية ستشعر بها في كل رشفة.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=creamy%20caffe%20latte%20in%20tall%20transparent%20glass%20cup%20showing%20beautiful%20layers%20of%20espresso%20and%20steamed%20milk%20with%20delicate%20foam%20art%20heart%20on%20top%20simple%20white%20background%20professional%20coffee%20photography%20natural%20daylight%20side%20angle&width=400&height=400&seq=menu-latte-009&orientation=squarish'
      },
      {
        id: 10,
        name: 'Mocha',
        nameEn: 'Mocha',
        nameAr: 'موكا',
        description: 'Kahve ve çikolatanın muhteşem buluşması mocha. Espresso, sıcak çikolata ve süt köpüğünün lezzetli uyumu. Tatlı kaçamaklar için ideal.',
        descriptionEn: 'Mocha, the magnificent meeting of coffee and chocolate. Delicious harmony of espresso, hot chocolate and milk foam. Ideal for sweet escapes.',
        descriptionAr: 'موكا، اللقاء الرائع بين القهوة والشوكولاتة. تناغم لذيذ من الإسبريسو والشوكولاتة الساخنة ورغوة الحليب. مثالي للهروب الحلو.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=rich%20chocolate%20mocha%20coffee%20in%20clear%20glass%20mug%20with%20whipped%20cream%20topping%20and%20chocolate%20syrup%20drizzle%20showing%20layers%20of%20espresso%20chocolate%20and%20milk%20simple%20background%20indulgent%20beverage%20photography%20warm%20lighting%20close%20up&width=400&height=400&seq=menu-mocha-010&orientation=squarish'
      },
      {
        id: 11,
        name: 'Sıcak Çikolata',
        nameEn: 'Hot Chocolate',
        nameAr: 'شوكولاتة ساخنة',
        description: 'Gerçek çikolatadan hazırlanan sıcak çikolata. Yoğun ve kremsi dokusuyla sizi ısıtacak. Üzerindeki krema ve çikolata parçalarıyla tam bir şölen.',
        descriptionEn: 'Hot chocolate made from real chocolate. Will warm you up with its thick and creamy texture. A complete feast with whipped cream and chocolate pieces on top.',
        descriptionAr: 'شوكولاتة ساخنة مصنوعة من الشوكولاتة الحقيقية. ستدفئك بقوامها الكثيف والكريمي. وليمة كاملة مع الكريمة المخفوقة وقطع الشوكولاتة في الأعلى.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=luxurious%20hot%20chocolate%20in%20ceramic%20mug%20with%20fluffy%20whipped%20cream%20and%20chocolate%20shavings%20on%20top%20rich%20dark%20brown%20color%20with%20cocoa%20powder%20dusting%20simple%20clean%20background%20cozy%20beverage%20photography%20soft%20warm%20lighting%20front%20view&width=400&height=400&seq=menu-hot-choco-011&orientation=squarish'
      },
      {
        id: 12,
        name: 'Sütlü Kahve',
        nameEn: 'Milk Coffee',
        nameAr: 'قهوة بالحليب',
        description: 'Kahve ve sütün klasik buluşması. Yumuşak ve dengeli tadıyla her yaştan kahve severin favorisi. Rahatlatıcı ve içimi kolay.',
        descriptionEn: 'Classic meeting of coffee and milk. Favorite of coffee lovers of all ages with its smooth and balanced taste. Relaxing and easy to drink.',
        descriptionAr: 'اللقاء الكلاسيكي بين القهوة والحليب. المفضل لعشاق القهوة من جميع الأعمار بطعمه الناعم والمتوازن. مريح وسهل الشرب.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=smooth%20milk%20coffee%20in%20white%20cup%20with%20perfect%20blend%20of%20coffee%20and%20steamed%20milk%20light%20caramel%20brown%20color%20served%20on%20saucer%20simple%20minimalist%20background%20comforting%20beverage%20photography%20natural%20soft%20lighting%20overhead&width=400&height=400&seq=menu-sutlu-012&orientation=squarish'
      },
      {
        id: 13,
        name: 'Espresso',
        nameEn: 'Espresso',
        nameAr: 'إسبريسو',
        description: 'Kahvenin en saf hali, espresso. Yoğun aroması ve altın renkli kreması ile gerçek kahve deneyimi. Kahve tutkunları için vazgeçilmez.',
        descriptionEn: 'The purest form of coffee, espresso. Real coffee experience with intense aroma and golden crema. Indispensable for coffee enthusiasts.',
        descriptionAr: 'الشكل الأنقى للقهوة، الإسبريسو. تجربة قهوة حقيقية برائحة قوية وكريما ذهبية. لا غنى عنه لعشاق القهوة.',
        price: '140 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=perfect%20espresso%20shot%20in%20small%20white%20ceramic%20demitasse%20cup%20with%20golden%20crema%20on%20top%20served%20on%20matching%20saucer%20with%20spoon%20simple%20clean%20background%20professional%20coffee%20photography%20dramatic%20lighting%20highlighting%20crema%20texture%20close%20up&width=400&height=400&seq=menu-espresso-013&orientation=squarish'
      },
      {
        id: 14,
        name: 'Americano',
        nameEn: 'Americano',
        nameAr: 'أمريكانو',
        description: 'Espresso ve sıcak suyun mükemmel dengesi americano. Yumuşak ve uzun içimlik. Kahvenin saf tadını seven kahve tutkunları için ideal.',
        descriptionEn: 'Perfect balance of espresso and hot water, americano. Smooth and long-lasting drink. Ideal for coffee enthusiasts who love the pure taste of coffee.',
        descriptionAr: 'التوازن المثالي بين الإسبريسو والماء الساخن، أمريكانو. مشروب ناعم وطويل الأمد. مثالي لعشاق القهوة الذين يحبون الطعم النقي للقهوة.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=classic%20americano%20coffee%20in%20large%20white%20ceramic%20mug%20with%20rich%20dark%20color%20and%20thin%20crema%20layer%20served%20on%20clean%20wooden%20surface%20simple%20background%20professional%20coffee%20shop%20photography%20bright%20natural%20lighting%20side%20view&width=400&height=400&seq=menu-americano-014&orientation=squarish'
      },
      {
        id: 15,
        name: 'Sahlep',
        nameEn: 'Sahlep',
        nameAr: 'سحلب',
        description: 'Kış günlerinin vazgeçilmezi sahlep. Salep kökünden hazırlanan, tarçınlı ve sıcacık. Sizi içten ısıtacak geleneksel Türk içeceği.',
        descriptionEn: 'Indispensable of winter days, sahlep. Prepared from salep root, with cinnamon and hot. Traditional Turkish drink that will warm you from inside.',
        descriptionAr: 'السحلب، العنصر الأساسي في أيام الشتاء. محضر من جذر السحلب، مع القرفة والساخن. مشروب تركي تقليدي سيدفئك من الداخل.',
        price: '180 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=traditional%20turkish%20sahlep%20hot%20drink%20in%20clear%20glass%20cup%20with%20cinnamon%20powder%20sprinkled%20on%20top%20creamy%20white%20color%20served%20with%20cinnamon%20stick%20garnish%20simple%20background%20authentic%20winter%20beverage%20photography%20warm%20cozy%20lighting%20steam%20rising&width=400&height=400&seq=menu-sahlep-015&orientation=squarish'
      },
      {
        id: 16,
        name: 'Filtre Kahve',
        nameEn: 'Filter Coffee',
        nameAr: 'قهوة مفلترة',
        description: 'Özenle seçilmiş kahve çekirdeklerinden damla damla süzülen filtre kahve. Kahvenin tüm aromasını ve notalarını hissedeceğiniz özel bir demleme.',
        descriptionEn: 'Filter coffee dripped drop by drop from carefully selected coffee beans. A special brew where you will feel all the aroma and notes of coffee.',
        descriptionAr: 'قهوة مفلترة تقطر قطرة قطرة من حبوب البن المختارة بعناية. مشروب خاص حيث ستشعر بكل رائحة ونكهات القهوة.',
        price: '180 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=pour%20over%20filter%20coffee%20in%20modern%20ceramic%20cup%20with%20clear%20dark%20brown%20color%20and%20aromatic%20steam%20rising%20served%20on%20wooden%20surface%20with%20coffee%20beans%20scattered%20simple%20clean%20background%20artisan%20coffee%20photography%20natural%20morning%20light&width=400&height=400&seq=menu-filtre-016&orientation=squarish'
      }
    ],
    cay: [
      {
        id: 17,
        name: 'Bardak Çay',
        nameEn: 'Glass Tea',
        nameAr: 'شاي بالكأس',
        description: 'Geleneksel ince belli bardakta sunulan demli çayımız. Türk çay kültürünün vazgeçilmezi. Taze demlenmiş, kıvamında ve aromalı.',
        descriptionEn: 'Our brewed tea served in traditional tulip-shaped glass. Indispensable of Turkish tea culture. Freshly brewed, perfect consistency and aromatic.',
        descriptionAr: 'شاينا المخمر يقدم في كأس تقليدي على شكل زهرة التوليب. لا غنى عنه في ثقافة الشاي التركية. طازج، قوام مثالي وعطري.',
        price: '60 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=traditional%20turkish%20tea%20in%20classic%20tulip%20shaped%20glass%20with%20deep%20red%20amber%20color%20served%20on%20small%20brass%20saucer%20with%20two%20sugar%20cubes%20simple%20white%20background%20authentic%20tea%20photography%20warm%20natural%20lighting%20side%20angle&width=400&height=400&seq=menu-bardak-cay-017&orientation=squarish'
      },
      {
        id: 18,
        name: 'Fincan Çay',
        nameEn: 'Cup Tea',
        nameAr: 'شاي بالفنجان',
        description: 'Porselen fincanda sunulan özel çayımız. Daha zarif bir sunum ve uzun süre sıcak kalma özelliği. Özel anlarınız için ideal.',
        descriptionEn: 'Our special tea served in porcelain cup. More elegant presentation and stays hot longer. Ideal for your special moments.',
        descriptionAr: 'شاينا الخاص يقدم في فنجان من البورسلين. عرض أكثر أناقة ويبقى ساخناً لفترة أطول. مثالي للحظاتك الخاصة.',
        price: '100 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=elegant%20turkish%20tea%20served%20in%20delicate%20porcelain%20cup%20and%20saucer%20with%20rich%20amber%20color%20and%20steam%20rising%20with%20ornate%20spoon%20simple%20clean%20background%20refined%20tea%20service%20photography%20soft%20afternoon%20lighting%20overhead%20view&width=400&height=400&seq=menu-fincan-cay-018&orientation=squarish'
      },
      {
        id: 19,
        name: 'Ihlamur',
        nameEn: 'Linden Tea',
        nameAr: 'شاي الزيزفون',
        description: 'Rahatlatıcı etkisiyle bilinen ıhlamur çayı. Doğal ve aromatik. Stresli günlerin sonunda kendinize bir mola hediye edin.',
        descriptionEn: 'Linden tea known for its relaxing effect. Natural and aromatic. Give yourself a break at the end of stressful days.',
        descriptionAr: 'شاي الزيزفون المعروف بتأثيره المريح. طبيعي وعطري. امنح نفسك استراحة في نهاية الأيام المجهدة.',
        price: '160 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=soothing%20linden%20tea%20ihlamur%20in%20clear%20glass%20teacup%20with%20golden%20yellow%20color%20and%20dried%20linden%20flowers%20floating%20served%20with%20honey%20jar%20simple%20background%20herbal%20tea%20photography%20natural%20calming%20light%20front%20view&width=400&height=400&seq=menu-ihlamur-019&orientation=squarish'
      },
      {
        id: 20,
        name: 'Melisa Çayı',
        nameEn: 'Lemon Balm Tea',
        nameAr: 'شاي المليسة',
        description: 'Ferahlatıcı ve sakinleştirici melisa çayı. Limon aromalı bu özel bitki çayı, zihin açıcı etkisiyle dikkat çeker. Doğal ve sağlıklı.',
        descriptionEn: 'Refreshing and calming lemon balm tea. This special herbal tea with lemon aroma stands out with its mind-clearing effect. Natural and healthy.',
        descriptionAr: 'شاي المليسة المنعش والمهدئ. هذا الشاي العشبي الخاص برائحة الليمون يبرز بتأثيره المنعش للعقل. طبيعي وصحي.',
        price: '180 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=refreshing%20lemon%20balm%20tea%20melissa%20in%20glass%20teacup%20with%20light%20green%20color%20and%20fresh%20melissa%20leaves%20garnish%20served%20on%20wooden%20board%20simple%20white%20background%20herbal%20wellness%20tea%20photography%20bright%20natural%20lighting%20close%20up&width=400&height=400&seq=menu-melisa-020&orientation=squarish'
      },
      {
        id: 21,
        name: 'Kuşburnu Çayı',
        nameEn: 'Rosehip Tea',
        nameAr: 'شاي ثمر الورد',
        description: 'C vitamini deposu kuşburnu çayı. Bağışıklık sisteminizi güçlendiren, ekşimsi ve ferahlatıcı tadıyla sağlıklı bir seçim.',
        descriptionEn: 'Rosehip tea, a vitamin C powerhouse. A healthy choice that strengthens your immune system with its slightly sour and refreshing taste.',
        descriptionAr: 'شاي ثمر الورد، مخزن فيتامين سي. خيار صحي يقوي جهاز المناعة بطعمه الحامض قليلاً والRefreshing beverage simple white background casual drink photography bright cheerful lighting&width=400&height=400&seq=menu-elma-oralet-025&orientation=squarish'
      },
      {
        id: 26,
        name: 'Kuşburnu - Kivi',
        nameEn: 'Rosehip - Kiwi',
        nameAr: 'ثمر الورد - كيوي',
        description: 'Kuşburnu ve kivinin C vitamini dolu buluşması. Ekşi ve tatlının mükemmel dengesi. Bağışıklık dostu, lezzetli bir içecek.',
        descriptionEn: 'Vitamin C-packed meeting of rosehip and kiwi. Perfect balance of sour and sweet. Immune-friendly, delicious drink.',
        descriptionAr: 'لقاء ثمر الورد والكيوي المليء بفيتامين سي. توازن مثالي بين الحامض والحلو. مشروب لذيذ صديق للمناعة.',
        price: '90 ₺',
        icon: 'ri-cup-fill',
        image: 'https://readdy.ai/api/search-image?query=rosehip%20kiwi%20instant%20tea%20in%20glass%20with%20vibrant%20red%20pink%20color%20and%20fresh%20kiwi%20slice%20garnish%20with%20kiwi%20half%20beside%20fruity%20refreshing%20drink%20simple%20clean%20background%20colorful%20beverage%20photography%20natural%20bright%20lighting%20side%20view&width=400&height=400&seq=menu-kusburnu-kivi-026&orientation=squarish'
      }
    ],
    soguk: [
      {
        id: 27,
        name: 'Buzlu Latte',
        nameEn: 'Iced Latte',
        nameAr: 'لاتيه مثلج',
        description: 'Yaz günlerinin vazgeçilmezi buzlu latte. Espresso ve soğuk sütün serinletici buluşması. Buz küpleriyle servis edilen ferahlatıcı bir kahve deneyimi.',
        descriptionEn: 'Indispensable of summer days, iced latte. Refreshing meeting of espresso and cold milk. A refreshing coffee experience served with ice cubes.',
        descriptionAr: 'لاتيه مثلج، العنصر الأساسي في أيام الصيف. لقاء منعش بين الإسبريسو والحليب البارد. تجربة قهوة منعشة تقدم مع مكعبات الثلج.',
        price: '160 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=iced%20latte%20in%20tall%20clear%20glass%20with%20ice%20cubes%20showing%20beautiful%20layers%20of%20espresso%20and%20milk%20with%20striped%20straw%20condensation%20on%20glass%20simple%20white%20background%20refreshing%20coffee%20drink%20photography%20bright%20summer%20lighting%20front%20view&width=400&height=400&seq=menu-buzlu-latte-027&orientation=squarish',
        popular: true
      },
      {
        id: 28,
        name: 'Buzlu Çikolata',
        nameEn: 'Iced Chocolate',
        nameAr: 'شوكولاتة مثلجة',
        description: 'Çikolata severlerin favorisi buzlu çikolata. Soğuk süt ve çikolatanın lezzetli karışımı. Üzerindeki krema ile tam bir tatlı kaçamağı.',
        descriptionEn: 'Favorite of chocolate lovers, iced chocolate. Delicious mix of cold milk and chocolate. A complete sweet escape with whipped cream on top.',
        descriptionAr: 'المفضل لعشاق الشوكولاتة، شوكولاتة مثلجة. مزيج لذيذ من الحليب البارد والشوكولاتة. هروب حلو كامل مع الكريمة المخفوقة في الأعلى.',
        price: '160 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=iced%20chocolate%20drink%20in%20tall%20glass%20filled%20with%20ice%20rich%20chocolate%20milk%20and%20whipped%20cream%20topping%20with%20chocolate%20syrup%20drizzle%20and%20straw%20simple%20background%20indulgent%20cold%20beverage%20photography%20cool%20lighting%20close%20up&width=400&height=400&seq=menu-buzlu-cikolata-028&orientation=squarish'
      },
      {
        id: 29,
        name: 'Buzlu Mocha',
        nameEn: 'Iced Mocha',
        nameAr: 'موكا مثلجة',
        description: 'Kahve ve çikolatanın soğuk versiyonu. Espresso, çikolata ve soğuk sütün mükemmel uyumu. Kremalı sunumuyla göz ve damak zevki bir arada.',
        descriptionEn: 'Cold version of coffee and chocolate. Perfect harmony of espresso, chocolate and cold milk. Eye and palate pleasure together with creamy presentation.',
        descriptionAr: 'النسخة الباردة من القهوة والشوكولاتة. تناغم مثالي من الإسبريسو والشوكولاتة والحليب البارد. متعة للعين والحنك معاً مع العرض الكريمي.',
        price: '160 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=iced%20mocha%20coffee%20drink%20in%20tall%20clear%20glass%20filled%20with%20ice%20cubes%20showing%20beautiful%20layers%20of%20rich%20chocolate%20espresso%20and%20cold%20milk%20topped%20with%20generous%20whipped%20cream%20and%20chocolate%20drizzle%20with%20chocolate%20shavings%20on%20top%20condensation%20droplets%20on%20glass%20with%20brown%20striped%20straw%20simple%20clean%20white%20background%20professional%20coffee%20shop%20photography%20bright%20natural%20lighting%20side%20angle%20view&width=400&height=400&seq=menu-buzlu-mocha-029-v2&orientation=squarish'
      },
      {
        id: 30,
        name: 'Buzlu Americano',
        nameEn: 'Iced Americano',
        nameAr: 'أمريكانو مثلج',
        description: 'Sade ve serinletici buzlu americano. Espresso ve soğuk suyun minimalist buluşması. Kahvenin saf tadını soğuk olarak deneyimleyin.',
        descriptionEn: 'Simple and refreshing iced americano. Minimalist meeting of espresso and cold water. Experience the pure taste of coffee cold.',
        descriptionAr: 'أمريكانو مثلج بسيط ومنعش. لقاء بسيط بين الإسبريسو والماء البارد. جرب الطعم النقي للقهوة بارداً.',
        price: '160 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=iced%20americano%20in%20tall%20glass%20filled%20with%20ice%20cubes%20and%20dark%20espresso%20with%20water%20minimalist%20presentation%20with%20black%20straw%20water%20droplets%20on%20glass%20simple%20background%20refreshing%20coffee%20photography%20bright%20clean%20lighting%20overhead&width=400&height=400&seq=menu-buzlu-americano-030&orientation=squarish'
      },
      {
        id: 31,
        name: 'Portakal Suyu',
        nameEn: 'Orange Juice',
        nameAr: 'عصير برتقال',
        description: 'Taze sıkılmış portakal suyu. C vitamini deposu, doğal ve sağlıklı. Her yudumda taze portakalın enerjisi.',
        descriptionEn: 'Freshly squeezed orange juice. Vitamin C powerhouse, natural and healthy. Energy of fresh oranges in every sip.',
        descriptionAr: 'عصير برتقال طازج. مخزن فيتامين سي، طبيعي وصحي. طاقة البرتقال الطازج في كل رشفة.',
        price: '180 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=fresh%20squeezed%20orange%20juice%20in%20clear%20glass%20with%20vibrant%20orange%20color%20and%20orange%20slice%20garnish%20with%20ice%20cubes%20and%20fresh%20oranges%20beside%20simple%20white%20background%20healthy%20fresh%20juice%20photography%20bright%20sunny%20lighting%20side%20view&width=400&height=400&seq=menu-portakal-031&orientation=squarish'
      },
      {
        id: 32,
        name: 'Limonata',
        nameEn: 'Lemonade',
        nameAr: 'ليمونادة',
        description: 'Ev yapımı taze limonata. Limon, nane ve buzun ferahlatıcı buluşması. Yaz sıcaklarının en iyi dostu.',
        descriptionEn: 'Homemade fresh lemonade. Refreshing meeting of lemon, mint and ice. Best friend of summer heat.',
        descriptionAr: 'ليمونادة طازجة منزلية الصنع. لقاء منعش بين الليمون والنعناع والثلج. أفضل صديق لحرارة الصيف.',
        price: '160 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=refreshing%20lemonade%20in%20tall%20glass%20with%20ice%20cubes%20lemon%20slices%20and%20fresh%20mint%20leaves%20pale%20yellow%20color%20with%20condensation%20droplets%20on%20glass%20simple%20background%20summer%20drink%20photography%20bright%20natural%20lighting%20front%20view&width=400&height=400&seq=menu-limonata-032&orientation=squarish'
      },
      {
        id: 33,
        name: 'Kola',
        nameEn: 'Cola',
        nameAr: 'كولا',
        description: 'Klasik kola içeceği. Buz gibi soğuk, gazlı ve ferahlatıcı. Her zaman popüler olan klasik seçim.',
        descriptionEn: 'Classic cola drink. Ice cold, carbonated and refreshing. Always popular classic choice.',
        descriptionAr: 'مشروب الكولا الكلاسيكي. بارد كالثلج، غازي ومنعش. الخيار الكلاسيكي الشائع دائماً.',
        price: '95 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=cola%20soft%20drink%20in%20glass%20with%20ice%20cubes%20and%20bubbles%20dark%20brown%20color%20with%20lemon%20wedge%20garnish%20condensation%20on%20glass%20with%20straw%20simple%20clean%20background%20refreshing%20soda%20photography%20cool%20lighting%20close%20up&width=400&height=400&seq=menu-kola-033&orientation=squarish'
      },
      {
        id: 34,
        name: 'Fanta',
        nameEn: 'Fanta',
        nameAr: 'فانتا',
        description: 'Portakal aromalı gazlı içecek. Renkli, neşeli ve ferahlatıcı. Meyve severlerin gazlı tercihi.',
        descriptionEn: 'Orange flavored carbonated drink. Colorful, cheerful and refreshing. Carbonated choice of fruit lovers.',
        descriptionAr: 'مشروب غازي بنكهة البرتقال. ملون، مبهج ومنعش. الخيار الغازي لعشاق الفواكه.',
        price: '95 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=orange%20soda%20fanta%20in%20glass%20with%20ice%20cubes%20bright%20vibrant%20orange%20color%20with%20bubbles%20and%20orange%20slice%20garnish%20with%20straw%20simple%20white%20background%20colorful%20refreshing%20drink%20photography%20vibrant%20lighting%20diagonal%20view&width=400&height=400&seq=menu-fanta-034&orientation=squarish'
      },
      {
        id: 35,
        name: 'Sprite',
        nameEn: 'Sprite',
        nameAr: 'سبرايت',
        description: 'Limon-lime aromalı gazlı içecek. Berrak, ferahlatıcı ve serinletici. Susuzluğunuzu giderecek hafif seçenek.',
        descriptionEn: 'Lemon-lime flavored carbonated drink. Clear, refreshing and cooling. Light option to quench your thirst.',
        descriptionAr: 'مشروب غازي بنكهة الليمون والليم. صافٍ، منعش ومبرد. خيار خفيف لإرواء عطشك.',
        price: '95 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=lemon%20lime%20soda%20sprite%20in%20clear%20glass%20with%20ice%20cubes%20and%20bubbles%20with%20lime%20slice%20garnish%20crystal%20clear%20color%20with%20water%20droplets%20simple%20background%20crisp%20refreshing%20drink%20photography%20bright%20clean%20lighting%20overhead&width=400&height=400&seq=menu-sprite-035&orientation=squarish'
      },
      {
        id: 36,
        name: 'Meyve Suları',
        nameEn: 'Fruit Juices',
        nameAr: 'عصائر الفواكه',
        description: 'Çeşitli meyve suları. Şeftali, vişne, kayısı gibi farklı tatlar. Doğal ve vitamin dolu seçenekler.',
        descriptionEn: 'Various fruit juices. Different flavors like peach, cherry, apricot. Natural and vitamin-packed options.',
        descriptionAr: 'عصائر فواكه متنوعة. نكهات مختلفة مثل الخوخ والكرز والمشمش. خيارات طبيعية ومليئة بالفيتامينات.',
        price: '95 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=assorted%20fruit%20juices%20in%20three%20glasses%20showing%20different%20colors%20orange%20red%20and%20yellow%20with%20fresh%20fruit%20garnishes%20arranged%20together%20simple%20white%20background%20healthy%20beverage%20variety%20photography%20natural%20bright%20lighting%20composition&width=400&height=400&seq=menu-meyve-sulari-036&orientation=squarish'
      },
      {
        id: 37,
        name: 'Ice Tea',
        nameEn: 'Ice Tea',
        nameAr: 'شاي مثلج',
        description: 'Soğuk çay. Şeftali veya limon aromalı seçenekler. Hafif, ferahlatıcı ve serinletici.',
        descriptionEn: 'Iced tea. Peach or lemon flavored options. Light, refreshing and cooling.',
        descriptionAr: 'شاي مثلج. خيارات بنكهة الخوخ أو الليمون. خفيف، منعش ومبرد.',
        price: '95 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=iced%20tea%20in%20tall%20glass%20with%20ice%20cubes%20amber%20color%20with%20lemon%20slice%20and%20fresh%20mint%20leaves%20garnish%20condensation%20on%20glass%20with%20straw%20simple%20background%20refreshing%20tea%20beverage%20photography%20summer%20lighting%20side%20angle&width=400&height=400&seq=menu-ice-tea-037&orientation=squarish'
      },
      {
        id: 38,
        name: 'Soda',
        nameEn: 'Soda',
        nameAr: 'صودا',
        description: 'Sade soda. Limon ve nane ile servis edilir. Ferahlatıcı ve sindirimi kolaylaştırıcı.',
        descriptionEn: 'Plain soda. Served with lemon and mint. Refreshing and aids digestion.',
        descriptionAr: 'صودا عادية. تقدم مع الليمون والنعناع. منعشة وتساعد على الهضم.',
        price: '70 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=plain%20soda%20water%20in%20clear%20glass%20with%20ice%20cubes%20and%20bubbles%20crystal%20clear%20with%20lemon%20wedge%20and%20mint%20leaf%20water%20droplets%20simple%20minimalist%20background%20refreshing%20sparkling%20water%20photography%20clean%20bright%20lighting%20front%20view&width=400&height=400&seq=menu-soda-038&orientation=squarish'
      },
      {
        id: 39,
        name: 'Meyveli Soda',
        nameEn: 'Fruit Soda',
        nameAr: 'صودا بالفواكه',
        description: 'Meyve aromalı soda. Çilek, ahududu gibi farklı meyve seçenekleri. Renkli ve lezzetli.',
        descriptionEn: 'Fruit flavored soda. Different fruit options like strawberry, raspberry. Colorful and delicious.',
        descriptionAr: 'مشروب غازي بنكهة الفواكه. خيارات فواكه مختلفة مثل الفراولة والتوت. ملونة ولذيذة.',
        price: '70 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=flavored%20fruit%20soda%20in%20glass%20with%20ice%20cubes%20light%20pink%20color%20with%20fresh%20strawberries%20and%20mint%20garnish%20sparkling%20bubbles%20rising%20simple%20background%20refreshing%20fruity%20drink%20photography%20bright%20natural%20light%20close%20up&width=400&height=400&seq=menu-meyveli-soda-039&orientation=squarish'
      },
      {
        id: 40,
        name: 'Çörcil (Soda+Limon)',
        nameEn: 'Çörcil (Soda+Lemon)',
        nameAr: 'تشورجيل (صودا+ليمون)',
        description: 'Geleneksel Türk içeceği çörcil. Soda ve taze limonun klasik buluşması. Ferahlatıcı ve sindirimi kolaylaştırıcı.',
        descriptionEn: 'Traditional Turkish drink çörcil. Classic meeting of soda and fresh lemon. Refreshing and aids digestion.',
        descriptionAr: 'مشروب تركي تقليدي تشورجيل. لقاء كلاسيكي بين الصودا والليمون الطازج. منعش ويساعد على الهضم.',
        price: '90 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=traditional%20turkish%20corcil%20drink%20soda%20with%20lemon%20in%20glass%20with%20ice%20cubes%20fresh%20lemon%20slices%20and%20mint%20leaves%20sparkling%20bubbles%20simple%20clean%20background%20refreshing%20local%20beverage%20photography%20bright%20summer%20lighting%20diagonal&width=400&height=400&seq=menu-corcil-040&orientation=squarish'
      },
      {
        id: 41,
        name: 'Su',
        nameEn: 'Water',
        nameAr: 'ماء',
        description: 'Saf içme suyu. Şişe veya bardak seçenekleri. Her zaman ihtiyacınız olan temel.',
        descriptionEn: 'Pure drinking water. Bottle or glass options. The essential you always need.',
        descriptionAr: 'ماء شرب نقي. خيارات الزجاجة أو الكأس. الأساسي الذي تحتاجه دائماً.',
        price: '30 ₺',
        icon: 'ri-goblet-fill',
        image: 'https://readdy.ai/api/search-image?query=pure%20bottled%20water%20in%20clear%20glass%20with%20ice%20cubes%20crystal%20clear%20water%20with%20condensation%20droplets%20on%20glass%20minimalist%20presentation%20simple%20white%20background%20clean%20refreshing%20water%20photography%20bright%20natural%20lighting%20overhead%20view&width=400&height=400&seq=menu-su-041&orientation=squarish'
      },
      {
        id: 42,
        name: 'Çilek Milkshake',
        nameEn: 'Strawberry Milkshake',
        nameAr: 'ميلك شيك الفراولة',
        description: 'Taze çileklerden hazırlanan milkshake. Kremsi, yoğun ve lezzetli. Üzerindeki krema ve çilek ile tam bir tatlı şöleni.',
        descriptionEn: 'Milkshake made from fresh strawberries. Creamy, thick and delicious. A complete sweet feast with whipped cream and strawberry on top.',
        descriptionAr: 'ميلك شيك مصنوع من الفراولة الطازجة. كريمي، كثيف ولذيذ. وليمة حلوة كاملة مع الكريمة المخفوقة والفراولة في الأعلى.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=strawberry%20milkshake%20in%20tall%20glass%20with%20pink%20creamy%20texture%20topped%20with%20whipped%20cream%20fresh%20strawberry%20and%20striped%20straw%20simple%20white%20background%20delicious%20dessert%20drink%20photography%20soft%20sweet%20lighting%20front%20view&width=400&height=400&seq=menu-cilek-shake-042&orientation=squarish',
        popular: true
      },
      {
        id: 43,
        name: 'Çikolata Milkshake',
        nameEn: 'Chocolate Milkshake',
        nameAr: 'ميلك شيك الشوكولاتة',
        description: 'Gerçek çikolatadan hazırlanan milkshake. Yoğun çikolata tadı, kremsi doku. Çikolata tutkunlarının favorisi.',
        descriptionEn: 'Milkshake made from real chocolate. Intense chocolate taste, creamy texture. Favorite of chocolate enthusiasts.',
        descriptionAr: 'ميلك شيك مصنوع من الشوكولاتة الحقيقية. طعم شوكولاتة قوي، قوام كريمي. المفضل لعشاق الشوكولاتة.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=chocolate%20milkshake%20in%20tall%20glass%20with%20rich%20brown%20creamy%20texture%20topped%20with%20whipped%20cream%20chocolate%20syrup%20drizzle%20and%20chocolate%20shavings%20with%20straw%20simple%20background%20indulgent%20dessert%20drink%20photography%20warm%20lighting%20close%20up&width=400&height=400&seq=menu-cikolata-shake-043&orientation=squarish',
        popular: true
      },
      {
        id: 44,
        name: 'Vanilya Milkshake',
        nameEn: 'Vanilla Milkshake',
        nameAr: 'ميلك شيك الفانيليا',
        description: 'Klasik vanilya milkshake. Sade ama lezzetli, kremsi ve yumuşak. Vanilyanın zarif aroması.',
        descriptionEn: 'Classic vanilla milkshake. Simple but delicious, creamy and smooth. Elegant aroma of vanilla.',
        descriptionAr: 'ميلك شيك الفانيليا الكلاسيكي. بسيط لكن لذيذ، كريمي وناعم. رائحة الفانيليا الأنيقة.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=vanilla%20milkshake%20in%20tall%20glass%20with%20creamy%20white%20texture%20topped%20with%20whipped%20cream%20and%20vanilla%20bean%20garnish%20with%20straw%20and%20vanilla%20pods%20beside%20simple%20clean%20background%20classic%20dessert%20drink%20photography%20bright%20soft%20lighting&width=400&height=400&seq=menu-vanilya-shake-044&orientation=squarish'
      },
      {
        id: 45,
        name: 'Karamel Milkshake',
        nameEn: 'Caramel Milkshake',
        nameAr: 'ميلك شيك الكراميل',
        description: 'Karamel soslu milkshake. Tatlı ve kremsi, karamelin yoğun tadı. Üzerindeki karamel sos ile görsel şölen.',
        descriptionEn: 'Milkshake with caramel sauce. Sweet and creamy, intense taste of caramel. Visual feast with caramel sauce on top.',
        descriptionAr: 'ميلك شيك مع صلصة الكراميل. حلو وكريمي، طعم الكراميل القوي. وليمة بصرية مع صلصة الكراميل في الأعلى.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=caramel%20milkshake%20in%20tall%20glass%20with%20golden%20brown%20creamy%20texture%20topped%20with%20whipped%20cream%20caramel%20drizzle%20and%20caramel%20pieces%20with%20straw%20simple%20background%20sweet%20dessert%20drink%20photography%20warm%20golden%20lighting%20diagonal%20view&width=400&height=400&seq=menu-karamel-shake-045&orientation=squarish'
      },
      {
        id: 46,
        name: 'Muz Milkshake',
        nameEn: 'Banana Milkshake',
        nameAr: 'ميلك شيك الموز',
        description: 'Taze muzdan hazırlanan milkshake. Doğal tatlılık, kremsi doku. Sağlıklı ve doyurucu bir seçenek.',
        descriptionEn: 'Milkshake made from fresh bananas. Natural sweetness, creamy texture. A healthy and satisfying option.',
        descriptionAr: 'ميلك شيك مصنوع من الموز الطازج. حلاوة طبيعية، قوام كريمي. خيار صحي ومُشبع.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=banana%20milkshake%20in%20tall%20glass%20with%20pale%20yellow%20creamy%20texture%20topped%20with%20whipped%20cream%20and%20fresh%20banana%20slice%20garnish%20with%20banana%20beside%20simple%20white%20background%20healthy%20dessert%20drink%20photography%20natural%20bright%20lighting%20side%20angle&width=400&height=400&seq=menu-muz-shake-046&orientation=squarish'
      },
      {
        id: 47,
        name: 'Y.Elma Frozen',
        nameEn: 'Green Apple Frozen',
        nameAr: 'تفاح أخضر مثلج',
        description: 'Yeşil elma aromalı frozen. Ekşi ve ferahlatıcı, buzlu doku. Yaz sıcaklarında serinleten lezzet.',
        descriptionEn: 'Green apple flavored frozen. Sour and refreshing, icy texture. Cooling flavor in summer heat.',
        descriptionAr: 'مثلج بنكهة التفاح الأخضر. حامض ومنعش، قوام جليدي. نكهة مبردة في حرارة الصيف.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=green%20apple%20frozen%20slush%20in%20tall%20glass%20with%20bright%20green%20icy%20texture%20topped%20with%20whipped%20cream%20and%20apple%20slice%20garnish%20with%20straw%20simple%20background%20refreshing%20frozen%20drink%20photography%20cool%20vibrant%20lighting%20front%20view&width=400&height=400&seq=menu-elma-frozen-047&orientation=squarish'
      },
      {
        id: 48,
        name: 'Çilek Frozen',
        nameEn: 'Strawberry Frozen',
        nameAr: 'فراولة مثلجة',
        description: 'Çilek aromalı frozen. Tatlı ve serinletici, buzlu çilek lezzeti. Renkli ve lezzetli bir seçenek.',
        descriptionEn: 'Strawberry flavored frozen. Sweet and cooling, icy strawberry flavor. A colorful and delicious option.',
        descriptionAr: 'مثلج بنكهة الفراولة. حلو ومبرد، نكهة الفراولة الجليدية. خيار ملون ولذيذ.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=strawberry%20frozen%20slush%20in%20tall%20glass%20with%20vibrant%20pink%20icy%20texture%20topped%20with%20whipped%20cream%20and%20fresh%20strawberry%20garnish%20with%20straw%20simple%20white%20background%20refreshing%20frozen%20dessert%20photography%20bright%20cool%20lighting%20close%20up&width=400&height=400&seq=menu-cilek-frozen-048&orientation=squarish'
      },
      {
        id: 49,
        name: 'Karpuz Frozen',
        nameEn: 'Watermelon Frozen',
        nameAr: 'بطيخ مثلج',
        description: 'Karpuz aromalı frozen. Ferahlatıcı ve serinletici, doğal meyve tadı. Yazın vazgeçilmez lezzeti.',
        descriptionEn: 'Watermelon flavored frozen. Refreshing and cooling, natural fruit taste. Indispensable flavor of summer.',
        descriptionAr: 'مثلج بنكهة البطيخ. منعش ومبرد، طعم الفاكهة الطبيعي. نكهة الصيف التي لا غنى عنها.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=watermelon%20frozen%20slush%20in%20tall%20glass%20with%20bright%20red%20icy%20texture%20topped%20with%20whipped%20cream%20and%20watermelon%20wedge%20garnish%20with%20straw%20simple%20background%20summer%20frozen%20drink%20photography%20vibrant%20refreshing%20lighting%20diagonal&width=400&height=400&seq=menu-karpuz-frozen-049&orientation=squarish'
      },
      {
        id: 50,
        name: 'Orman Meyveli Frozen',
        nameEn: 'Forest Fruit Frozen',
        nameAr: 'فواكه الغابة مثلجة',
        description: 'Karışık orman meyveli frozen. Çilek, ahududu, böğürtlen karışımı. Antioksidan deposu, lezzetli ve sağlıklı.',
        descriptionEn: 'Mixed forest fruit frozen. Strawberry, raspberry, blackberry mix. Antioxidant powerhouse, delicious and healthy.',
        descriptionAr: 'مثلج فواكه الغابة المختلطة. مزيج الفراولة والتوت والعليق. مخزن مضادات الأكسدة، لذيذ وصحي.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=mixed%20berry%20forest%20fruit%20frozen%20slush%20in%20tall%20glass%20with%20deep%20purple%20red%20icy%20texture%20topped%20with%20whipped%20cream%20and%20fresh%20berries%20garnish%20with%20straw%20simple%20background%20antioxidant%20frozen%20drink%20photography%20rich%20lighting%20overhead&width=400&height=400&seq=menu-orman-frozen-050&orientation=squarish'
      },
      {
        id: 51,
        name: 'Muz Frozen',
        nameEn: 'Banana Frozen',
        nameAr: 'موز مثلج',
        description: 'Muz aromalı frozen. Kremsi ve yumuşak doku, doğal tatlılık. Sağlıklı ve doyurucu bir tatlı alternatifi.',
        descriptionEn: 'Banana flavored frozen. Creamy and smooth texture, natural sweetness. A healthy and satisfying dessert alternative.',
        descriptionAr: 'مثلج بنكهة الموز. قوام كريمي وناعم، حلاوة طبيعية. بديل حلوى صحي ومُشبع.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=banana%20frozen%20slush%20in%20tall%20glass%20with%20pale%20yellow%20icy%20smooth%20texture%20topped%20with%20whipped%20cream%20and%20banana%20slice%20garnish%20with%20straw%20and%20banana%20beside%20simple%20clean%20background%20creamy%20frozen%20dessert%20photography%20soft%20natural%20lighting&width=400&height=400&seq=menu-muz-frozen-051&orientation=squarish'
      },
      {
        id: 52,
        name: 'Frambuaz Frozen',
        nameEn: 'Raspberry Frozen',
        nameAr: 'توت العليق مثلج',
        description: 'Frambuaz aromalı frozen. Ekşi ve tatlının mükemmel dengesi. Bağışıklık dostu, lezzetli bir içecek.',
        descriptionEn: 'Raspberry flavored frozen. Sweet-sour balance, intense fruit taste. Refreshing and delicious.',
        descriptionAr: 'مثلج بنكهة التوت. توازن حلو-حامض، طعم فاكهة قوي. منعش ولذيذ.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=raspberry%20frozen%20slush%20in%20tall%20glass%20with%20vibrant%20pink%20red%20icy%20texture%20topped%20with%20whipped%20cream%20and%20fresh%20raspberries%20garnish%20with%20straw%20simple%20background%20tangy%20frozen%20drink%20photography%20bright%20berry%20lighting%20side%20view&width=400&height=400&seq=menu-frambuaz-frozen-052&orientation=squarish'
      },
      {
        id: 53,
        name: 'Böğürtlen Frozen',
        nameEn: 'Blackberry Frozen',
        nameAr: 'توت أسود مثلج',
        description: 'Böğürtlen aromalı frozen. Yoğun meyve tadı, koyu renk. Antioksidan açısından zengin, sağlıklı bir seçenek.',
        descriptionEn: 'Blackberry flavored frozen. Intense fruit taste, dark color. Rich in antioxidants, a healthy choice.',
        descriptionAr: 'مثلج بنكهة التوت الأسود. طعم فاكهة قوي، لون داكن. غني بمضادات الأكسدة، خيار صحي.',
        price: '180 ₺',
        icon: 'ri-cake-3-fill',
        image: 'https://readdy.ai/api/search-image?query=blackberry%20frozen%20slush%20in%20tall%20glass%20with%20deep%20purple%20icy%20texture%20topped%20with%20whipped%20cream%20and%20fresh%20blackberries%20garnish%20with%20straw%20simple%20white%20background%20rich%20frozen%20dessert%20photography%20dramatic%20berry%20lighting%20close%20up&width=400&height=400&seq=menu-bogurtlen-frozen-053&orientation=squarish'
      }
    ],
    pasta: [
      {
        id: 54,
        name: 'Orman Meyveli Pasta',
        nameEn: 'Forest Fruit Cake',
        nameAr: 'كيك فواكه الغابة',
        description: 'Taze orman meyveleriyle süslenmiş özel pastamız. Pandispanya katları arasında kremalı dolgu ve bol meyve. Her dilimde farklı meyvelerin tadını alacağınız renkli bir lezzet.',
        descriptionEn: 'Our special cake decorated with fresh forest fruits. Creamy filling and plenty of fruit between sponge layers. A colorful flavor where you will taste different fruits in every slice.',
        descriptionAr: 'كيكتنا الخاصة المزينة بفواكه الغابة الطازجة. حشوة كريمية والكثير من الفواكه بين طبقات الإسفنج. نكهة ملونة حيث ستتذوق فواكه مختلفة في كل شريحة.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=forest%20fruit%20cake%20slice%20with%20layers%20of%20vanilla%20sponge%20cream%20and%20mixed%20berries%20topped%20with%20fresh%20strawberries%20blueberries%20and%20raspberries%20on%20white%20plate%20with%20fork%20simple%20clean%20background%20elegant%20dessert%20photography%20natural%20lighting%20side%20angle&width=400&height=400&seq=menu-orman-pasta-054&orientation=squarish',
        popular: true
      },
      {
        id: 55,
        name: 'Siyah Profiterollü Pasta',
        nameEn: 'Black Profiterole Cake',
        nameAr: 'كيك بروفيترول أسود',
        description: 'Çikolata tutkunlarının favorisi. Mini profiteroller ve yoğun çikolata kreması ile hazırlanan özel pastamız. Üzerindeki çikolata sosu ile tam bir çikolata şöleni.',
        descriptionEn: 'Favorite of chocolate lovers. Our special cake prepared with mini profiteroles and rich chocolate cream. A complete chocolate feast with chocolate sauce on top.',
        descriptionAr: 'المفضل لعشاق الشوكولاتة. كيكتنا الخاصة المحضرة مع البروفيترول الصغير وكريمة الشوكولاتة الغنية. وليمة شوكولاتة كاملة مع صلصة الشوكولاتة في الأعلى.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=black%20profiterole%20cake%20slice%20with%20chocolate%20cream%20puffs%20and%20rich%20chocolate%20ganache%20layers%20topped%20with%20chocolate%20sauce%20and%20profiteroles%20on%20white%20plate%20simple%20background%20decadent%20dessert%20photography%20dramatic%20chocolate%20lighting%20front%20view&width=400&height=400&seq=menu-profiterol-pasta-055&orientation=squarish',
        popular: true
      },
      {
        id: 56,
        name: 'Ballı Cevizli',
        nameEn: 'Honey Walnut Cake',
        nameAr: 'كيك العسل والجوز',
        description: 'Geleneksel lezzetlerin modern yorumu. Ballı krema ve taze cevizlerle hazırlanan özel pastamız. Doğal tatlandırıcı bal ve cevizin mükemmel uyumu.',
        descriptionEn: 'Modern interpretation of traditional flavors. Our special cake prepared with honey cream and fresh walnuts. Perfect harmony of natural sweetener honey and walnuts.',
        descriptionAr: 'تنسيق حديث للنكهات التقليدية. كيكتنا الخاصة المحضرة مع كريمة العسل والجوز الطازج. تناغم مثالي بين العسل الطبيعي والجوز.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=honey%20walnut%20cake%20slice%20with%20layers%20of%20sponge%20honey%20cream%20and%20crushed%20walnuts%20topped%20with%20whole%20walnuts%20and%20honey%20drizzle%20on%20plate%20with%20honey%20dipper%20simple%20background%20traditional%20dessert%20photography%20warm%20golden%20lighting%20close%20up&width=400&height=400&seq=menu-balli-ceviz-056&orientation=squarish'
      },
      {
        id: 57,
        name: 'Tiramisu',
        nameEn: 'Tiramisu',
        nameAr: 'تيراميسو',
        description: 'İtalyan mutfağının efsane tatlısı. Kahve ile ıslatılmış kedi dili bisküvileri ve mascarpone peyniri kreması. Üzerindeki kakao tozu ile klasik sunum.',
        descriptionEn: 'Legendary dessert of Italian cuisine. Ladyfinger biscuits soaked in coffee and mascarpone cheese cream. Classic presentation with cocoa powder on top.',
        descriptionAr: 'الحلوى الأسطورية للمطبخ الإيطالي. بسكويت أصابع السيدة المنقوع في القهوة وكريمة جبن الماسكاربوني. عرض كلاسيكي مع مسحوق الكاكاو في الأعلى.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=classic%20tiramisu%20slice%20with%20layers%20of%20coffee%20soaked%20ladyfingers%20and%20mascarpone%20cream%20dusted%20with%20cocoa%20powder%20on%20white%20plate%20with%20coffee%20beans%20simple%20clean%20background%20italian%20dessert%20photography%20elegant%20soft%20lighting%20overhead%20view&width=400&height=400&seq=menu-tiramisu-057&orientation=squarish',
        popular: true
      },
      {
        id: 58,
        name: 'Çikolatalı Pasta',
        nameEn: 'Chocolate Cake',
        nameAr: 'كيك الشوكولاتة',
        description: 'Klasik çikolatalı pasta. Yumuşak pandispanya katları ve yoğun çikolata kreması. Üzerindeki çikolata ganajı ve süslemeleri ile göz ve damak zevki bir arada.',
        descriptionEn: 'Classic chocolate cake. Soft sponge layers and rich chocolate cream. Eye and palate pleasure together with chocolate ganache and decorations on top.',
        descriptionAr: 'كيك الشوكولاتة الكلاسيكي. طبقات إسفنج ناعمة وكريمة شوكولاتة غنية. متعة للعين والحنك معاً مع غاناش الشوكولاتة والزينة في الأعلى.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=chocolate%20cake%20slice%20with%20rich%20chocolate%20sponge%20layers%20and%20chocolate%20cream%20topped%20with%20chocolate%20ganache%20and%20chocolate%20curls%20on%20plate%20with%20chocolate%20pieces%20simple%20background%20indulgent%20dessert%20photography%20deep%20chocolate%20lighting%20diagonal&width=400&height=400&seq=menu-cikolata-pasta-058&orientation=squarish'
      },
      {
        id: 59,
        name: 'Mozaik Pasta',
        nameEn: 'Mosaic Cake',
        nameAr: 'كيك الموزاييك',
        description: 'Nostaljik lezzet mozaik pasta. Bisküvi parçaları ve çikolatalı kremanın buluşması. Soğuk servis edilen, pratik ve lezzetli bir tatlı.',
        descriptionEn: 'Nostalgic flavor mosaic cake. Meeting of biscuit pieces and chocolate cream. A practical and delicious dessert served cold.',
        descriptionAr: 'كيك الموزاييك بنكهة الحنين. لقاء قطع البسكويت وكريمة الشوكولاتة. حلوى عملية ولذيذة تقدم باردة.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=mosaic%20cake%20slice%20showing%20colorful%20biscuit%20pieces%20in%20chocolate%20cream%20mixture%20with%20chocolate%20coating%20on%20white%20plate%20with%20biscuits%20beside%20simple%20background%20nostalgic%20turkish%20dessert%20photography%20warm%20inviting%20lighting%20side%20view&width=400&height=400&seq=menu-mozaik-059&orientation=squarish'
      },
      {
        id: 60,
        name: 'Frambuazlı Cheesecake',
        nameEn: 'Raspberry Cheesecake',
        nameAr: 'تشيز كيك التوت',
        description: 'Kremsi cheesecake ve taze frambuazların mükemmel uyumu. Bisküvi tabanı, yoğun peynir kreması ve üzerindeki frambuaz sosu. Hafif ekşi, yoğun kremsi.',
        descriptionEn: 'Perfect harmony of creamy cheesecake and fresh raspberries. Biscuit base, rich cheese cream and raspberry sauce on top. Slightly sour, intensely creamy.',
        descriptionAr: 'تناغم مثالي بين التشيز كيك الكريمي والتوت الطازج. قاعدة البسكويت، كريمة الجبن الغنية وصلصة التوت في الأعلى. حامض قليلاً، كريمي بشكل مكثف.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=raspberry%20cheesecake%20slice%20with%20creamy%20white%20cheese%20layer%20on%20graham%20cracker%20crust%20topped%20with%20fresh%20raspberries%20and%20raspberry%20sauce%20on%20plate%20with%20mint%20simple%20background%20elegant%20dessert%20photography%20bright%20berry%20lighting%20front%20view&width=400&height=400&seq=menu-frambuaz-cheese-060&orientation=squarish'
      },
      {
        id: 61,
        name: 'Fıstık Rüyası',
        nameEn: 'Pistachio Dream',
        nameAr: 'حلم الفستق',
        description: 'Antep fıstığının eşsiz lezzetiyle hazırlanan özel pastamız. Fıstıklı pandispanya ve fıstık kreması. Üzerindeki fıstık parçaları ile lüks bir sunum.',
        descriptionEn: 'Our special cake prepared with unique flavor of Antep pistachio. Pistachio sponge and pistachio cream. Luxurious presentation with pistachio pieces on top.',
        descriptionAr: 'كيكتنا الخاصة المحضرة بنكهة الفستق الحلبي الفريدة. إسفنج الفستق وكريمة الفستق. عرض فاخر مع قطع الفستق في الأعلى.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=pistachio%20dream%20cake%20slice%20with%20layers%20of%20pistachio%20sponge%20and%20pistachio%20cream%20topped%20with%20crushed%20pistachios%20and%20pistachio%20pieces%20on%20plate%20with%20pistachios%20scattered%20simple%20background%20luxurious%20turkish%20dessert%20photography%20green%20elegant%20lighting%20close%20up&width=400&height=400&seq=menu-fistik-ruya-061&orientation=squarish'
      },
      {
        id: 62,
        name: 'San Sebastian',
        nameEn: 'San Sebastian',
        nameAr: 'سان سيباستيان',
        description: 'Bask mutfağının ünlü yanık cheesecake\'i. Dışı karamelize, içi kremsi. Fırında yüksek ısıda pişirilen bu özel cheesecake, klasik cheesecake\'lerden farklı bir deneyim sunar.',
        descriptionEn: 'Famous burnt cheesecake of Basque cuisine. Caramelized outside, creamy inside. This special cheesecake baked at high temperature offers a different experience from classic cheesecakes.',
        descriptionAr: 'تشيز كيك المحروق الشهير من مطبخ الباسك. كراميل من الخارج، كريمي من الداخل. هذا التشيز كيك الخاص المخبوز في درجة حرارة عالية يقدم تجربة مختلفة عن التشيز كيك الكلاسيكي.',
        price: '290 ₺',
        icon: 'ri-cake-2-fill',
        image: 'https://readdy.ai/api/search-image?query=san%20sebastian%20basque%20burnt%20cheesecake%20slice%20with%20caramelized%20top%20and%20creamy%20interior%20on%20white%20plate%20with%20caramel%20sauce%20drizzle%20simple%20clean%20background%20artisan%20spanish%20dessert%20photography%20dramatic%20golden%20caramel%20lighting%20overhead&width=400&height=400&seq=menu-san-sebastian-062&orientation=squarish'
      }
    ]
  };

  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  const closeMenu = () => {
    setActiveCategory(null);
    setSelectedItem(null);
  };

  const getItemName = (item: MenuItem) => {
    if (i18n.language === 'en') return item.nameEn;
    if (i18n.language === 'ar') return item.nameAr;
    return item.name;
  };

  const getItemDescription = (item: MenuItem) => {
    if (i18n.language === 'en') return item.descriptionEn;
    if (i18n.language === 'ar') return item.descriptionAr;
    return item.description;
  };

  return (
    <section id="menu" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-amber-600 font-semibold text-sm tracking-wider uppercase">{t('menu.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('menu.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('menu.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className="aspect-square px-4 py-6 sm:px-8 sm:py-12 rounded-2xl font-medium transition-all duration-300 flex flex-col items-center justify-center space-y-2 sm:space-y-4 cursor-pointer bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 hover:shadow-xl border-2 border-amber-200 hover:border-amber-300 hover:scale-105"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-600 rounded-full flex items-center justify-center">
                <i className={`${category.icon} text-2xl sm:text-3xl text-white`}></i>
              </div>
              <span className="text-sm sm:text-lg font-bold text-gray-900 text-center leading-tight">{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {activeCategory && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeMenu}
        >
          <div 
            className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center">
                    <i className={`${categories.find(c => c.id === activeCategory)?.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {categories.find(c => c.id === activeCategory)?.name}
                  </h3>
                </div>
                <button
                  onClick={closeMenu}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-700"></i>
                </button>
              </div>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {menuItems[activeCategory].map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-amber-300 hover:shadow-xl transition-all duration-300 relative group cursor-pointer"
                  >
                    {item.popular && (
                      <div className="absolute top-3 right-3 bg-amber-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg z-10">
                        {t('menu.popular')}
                      </div>
                    )}
                    
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>

                    <div className="p-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-xl text-amber-600`}></i>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-amber-700 transition-colors">
                          {getItemName(item)}
                        </h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors z-10 cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-700"></i>
              </button>
              
              <div className="relative h-80 overflow-hidden rounded-t-3xl">
                <img 
                  src={selectedItem.image} 
                  alt={selectedItem.name}
                  className="w-full h-full object-cover"
                />
                {selectedItem.popular && (
                  <div className="absolute top-4 left-4 bg-amber-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg">
                    {t('menu.popular')}
                  </div>
                )}
              </div>

              <div className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${selectedItem.icon} text-2xl text-amber-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      {getItemName(selectedItem)}
                    </h3>
                    <div className="text-amber-700 font-bold text-2xl">
                      {selectedItem.price}
                    </div>
                  </div>
                </div>

                {getItemDescription(selectedItem) && (
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {getItemDescription(selectedItem)}
                  </p>
                )}

                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    {t('menu.vatIncluded')}
                  </p>
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="px-6 py-3 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                  >
                    {t('menu.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
